import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Lock } from "lucide-react";

const LOGO_URL = "https://genesisrecovery.ai/assets/newlogo2-euSTC0ph.png";

const COLS = [
    {
        title: "Product",
        links: [
            { label: "Features", href: "/#features" },
            { label: "How it Works", href: "/#how-it-works" },
            { label: "Pricing", href: "/#pricing" },
            { label: "Download", href: "/#download" },
        ],
    },
    {
        title: "Support",
        links: [
            { label: "Help Center", href: "/#faq" },
            { label: "Contact Us", href: "/#contact" },
            { label: "Privacy Policy", href: "/privacy-policy" },
            { label: "Terms of Service", href: "/terms-of-service" },
        ],
    },
];

function XIcon(props) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M18.244 2H21.5l-7.11 8.128L22.75 22h-6.547l-5.126-6.707L5.21 22H1.95l7.604-8.692L1.54 2h6.713l4.633 6.117L18.244 2Zm-1.148 18h1.804L7.278 3.895H5.344L17.096 20Z" />
        </svg>
    );
}

function TikTokIcon(props) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M14.68 3c.17 1.4.95 2.77 2.05 3.76 1.02.92 2.34 1.43 3.72 1.47v3.08a8.23 8.23 0 0 1-2.94-.54 8.57 8.57 0 0 1-2.15-1.18v6.04c0 1.53-.48 2.96-1.3 4.14a6.55 6.55 0 0 1-2.89 2.26 6.88 6.88 0 0 1-4.3.18 6.62 6.62 0 0 1-3.43-2.42A6.31 6.31 0 0 1 2 16.11a6.36 6.36 0 0 1 1.53-4.26 6.64 6.64 0 0 1 3.87-2.2c.48-.08.97-.1 1.45-.05v3.16a3.45 3.45 0 0 0-2.34.72 3.17 3.17 0 0 0-.95 3.6c.21.54.58 1 1.04 1.34a3.5 3.5 0 0 0 3.77.22 3.27 3.27 0 0 0 1.47-1.88c.08-.3.12-.61.12-.92V3h2.72Z" />
        </svg>
    );
}

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="bg-foreground text-background">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
                <div className="grid lg:grid-cols-[1.3fr_0.7fr_0.7fr] gap-10 md:gap-14 items-start">
                    <div>
                        <Link to="/" data-testid="footer-logo-link" className="inline-flex items-center gap-2 mb-5">
                            <img
                                src={LOGO_URL}
                                alt="Genesis Recovery"
                                className="h-[6.25rem] w-[15.5rem] object-contain object-left brightness-0 invert"
                            />
                        </Link>
                        <p className="text-background/70 max-w-sm leading-relaxed text-sm">
                            Your private, supportive companion for those on the path to
                            recovery. One day at a time, one step at a time.
                        </p>

                        <div className="mt-6 flex items-center gap-2">
                            {[
                                { Icon: XIcon, href: "https://x.com/GenesisRecAI", label: "X" },
                                {
                                    Icon: Facebook,
                                    href: "https://www.facebook.com/people/Genesis-Recovery-AI/61574386660333/",
                                    label: "Facebook",
                                },
                                {
                                    Icon: Instagram,
                                    href: "https://www.instagram.com/genesisrecoveryai/",
                                    label: "Instagram",
                                },
                                {
                                    Icon: TikTokIcon,
                                    href: "https://www.tiktok.com/@genesisrecoveryai",
                                    label: "TikTok",
                                },
                                {
                                    Icon: Linkedin,
                                    href: "https://www.linkedin.com/company/genesis-recovery-ai/about/?viewAsMember=true",
                                    label: "LinkedIn",
                                },
                            ].map(({ Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={label}
                                    data-testid={`social-${label.toLowerCase()}`}
                                    className="w-9 h-9 rounded-md border border-white/10 bg-white/5 flex items-center justify-center text-background/70 hover:text-background hover:bg-white/10 transition-colors"
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:col-span-2 gap-8 md:gap-12">
                        {COLS.map((col) => (
                            <div key={col.title}>
                                <h4 className="text-xs uppercase tracking-wider font-bold text-background mb-4">
                                    {col.title}
                                </h4>
                                <ul className="space-y-2.5">
                                    {col.links.map((l) => (
                                        <li key={l.label}>
                                            <a
                                                href={l.href}
                                                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="text-sm text-background/70 hover:text-background transition-colors"
                                            >
                                                {l.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <p className="text-xs text-background/60">
                        © {new Date().getFullYear()} Genesis Recovery AI. All rights reserved.
                    </p>
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs text-background/80">
                        <Lock className="w-3.5 h-3.5 text-emerald-300" />
                        256-bit Encryption
                    </div>
                </div>

                <div className="mt-5 text-center text-[11px] text-background/45 max-w-3xl mx-auto leading-relaxed">
                    Genesis Recovery AI is a support tool and does not replace professional medical or
                    mental health treatment. Please seek appropriate professional support whenever
                    you need immediate care.
                </div>
            </div>
        </footer>
    );
}
